import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'peakx-light' | 'peakx-dark';

interface ThemeStore {
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
}

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'peakx-light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'peakx-dark'
    : 'peakx-light';
};

const resolveTheme = (preference: ThemePreference): ResolvedTheme => {
  if (preference === 'system') return getSystemTheme();
  return preference === 'dark' ? 'peakx-dark' : 'peakx-light';
};

const THEME_COLORS: Record<ResolvedTheme, string> = {
  'peakx-light': '#ffffff',
  'peakx-dark': '#2a2a2a',
};

const applyTheme = (resolved: ResolvedTheme) => {
  document.documentElement.setAttribute('data-theme', resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', THEME_COLORS[resolved]);
  }
};

// Track the media query listener so we can clean up
let mediaQueryCleanup: (() => void) | null = null;

const setupSystemListener = (store: { getState: () => ThemeStore }) => {
  // Clean up previous listener
  if (mediaQueryCleanup) {
    mediaQueryCleanup();
    mediaQueryCleanup = null;
  }

  const state = store.getState();
  if (state.theme !== 'system') return;

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    const currentState = store.getState();
    if (currentState.theme === 'system') {
      const resolved = getSystemTheme();
      applyTheme(resolved);
      // Update resolvedTheme in store without triggering persist cycle
      useThemeStore.setState({ resolvedTheme: resolved });
    }
  };

  mediaQuery.addEventListener('change', handler);
  mediaQueryCleanup = () => mediaQuery.removeEventListener('change', handler);
};

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      theme: 'system',
      resolvedTheme: resolveTheme('system'),

      setTheme: (theme: ThemePreference) => {
        const resolved = resolveTheme(theme);
        applyTheme(resolved);
        set({ theme, resolvedTheme: resolved });
        // Re-setup system listener if switching to/from system
        setupSystemListener(useThemeStore);
      },
    }),
    {
      name: 'peakx-theme',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => {
        return (state) => {
          if (state) {
            const resolved = resolveTheme(state.theme);
            state.resolvedTheme = resolved;
            applyTheme(resolved);
            // Setup system listener after rehydration
            setTimeout(() => setupSystemListener(useThemeStore), 0);
          }
        };
      },
    }
  )
);

// Initialize: apply theme immediately on module load
const initialState = useThemeStore.getState();
applyTheme(resolveTheme(initialState.theme));
setupSystemListener(useThemeStore);
